import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Text, Layout, FlexContainer, ModalContext, Modals } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import EventService from 'Services/events';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import useTranslation from 'Hooks/useTranslation';
import {
  useFetchGetRecordResultXml,
  useFetchGetXmlRecord,
  useLoaderProgress,
} from 'Containers/Datagathering/components/QuizLoaderBanner/hooks';
import { TaxReturnTypes } from 'Containers/Result/utils';
import OneColLayout from 'Components/Layouts/OneColLayout';
import { TAX_YEAR } from 'Config';
import { routes } from 'Constants/routes';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import Progress from './ProgressBar';
import {
  useFetchPollingAnualDeclaration,
  useFetchPollingPreviousYears,
  useFetchReintents,
} from './hooks';
import * as Styled from './styles';

const TIME_OUT_MILLISECOND = 110000;

const DataGatheringLoader = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.DG);
  const { progress, timeRemaining } = useLoaderProgress();
  const { showModal, hideModal } = useContext(ModalContext);
  const isPreviousYears = useSelector(routeStateSelector);
  useFetchPollingPreviousYears(Boolean(isPreviousYears?.products?.isPreviousYear));
  useFetchPollingAnualDeclaration(!isPreviousYears?.products?.isPreviousYear);
  const { data: xmlRecord } = useFetchGetXmlRecord();
  const { data: resultXml } = useFetchGetRecordResultXml(Boolean(xmlRecord?.ready));
  const { data: reintents } = useFetchReintents({
    refetchInterval: data => {
      const response = data?.data;
      if (!response?.item) {
        return 500;
      }
      if (response?.item && response?.item?.executedAttempts < 2) {
        return 500;
      }
      return false;
    },
    enabled: !isPreviousYears?.products?.isPreviousYear,
  });

  const isXmlRecordReadyAndIsToReturn =
    xmlRecord?.ready &&
    resultXml?.taxReturnType === TaxReturnTypes.TO_RETURN &&
    resultXml?.flexUser;
  useEffect(() => {
    if (isXmlRecordReadyAndIsToReturn && !isPreviousYears?.products?.isPreviousYear) {
      history.push(routes.RESULT_XML);
    }
  }, [isXmlRecordReadyAndIsToReturn]);

  useEffect(() => {
    if (isPreviousYears?.products?.isPreviousYear !== undefined) {
      StorageService.setItem('isPreviousYear', isPreviousYears.products.isPreviousYear);
    }
  }, [isPreviousYears?.products?.isPreviousYear]);

  const handleClickModal = (): void => {
    hideModal();
    history.push(routes.DASHBOARD);
  };

  useEffect(() => {
    if (isPreviousYears?.products?.isPreviousYear) {
      const timer = setTimeout(() => {
        EventService.taxDataWaitTimeOut(
          TAX_YEAR,
          TIME_OUT_MILLISECOND,
          isPreviousYears?.products?.isPreviousYear
        );
        showModal({
          comp: (
            <Modals.Centered
              buttonsAlign="center"
              maxSize="XL"
              hasClose={false}
              urlImage={`${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/settings_s.png`}
              title={t('Dg.sat.modal.title')}
              subtitle={t('Dg.sat.modal.subtitle')}
              description={t('Dg.sat.modal.infotitle')}
              primaryButton={{
                label: t('Dg.sat.modal.button'),
                onClick: handleClickModal,
              }}
            />
          ),
        });
      }, TIME_OUT_MILLISECOND);

      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (
      reintents &&
      reintents?.data?.item?.executedAttempts > 1 &&
      !isPreviousYears?.products?.isPreviousYear
    ) {
      EventService.taxDataWaitTimeOut(
        TAX_YEAR,
        TIME_OUT_MILLISECOND,
        isPreviousYears?.products?.isPreviousYear,
        reintents?.data?.item?.executedAttempts
      );
      showModal({
        comp: (
          <Modals.Centered
            buttonsAlign="center"
            maxSize="XL"
            hasClose={false}
            urlImage={`${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/settings_s.png`}
            title={t('Dg.sat.modal.title')}
            subtitle={t('Dg.sat.modal.subtitle')}
            description={t('Dg.sat.modal.infotitle')}
            primaryButton={{
              label: t('Dg.sat.modal.button'),
              onClick: handleClickModal,
            }}
          />
        ),
      });
    }
  }, [reintents]);

  return (
    <OneColLayout>
      <Layout.Wrap fullWidth mode="success">
        <FlexContainer direction="column" align="center" fullHeight fullWidth justify="center">
          <Styled.Container direction="column" gap="dimension_16">
            <Text type="paragraphs" size="XL_L_M" color="dark">
              {t('Dg.loader.result.title')}
            </Text>
            <Text type="paragraphs" color="grey" size="M_M_M">
              {t('Dg.loader.result.subtitle')}
            </Text>
            <FlexContainer direction="column" gap="dimension_08">
              <Progress progress={progress} />
              <Text type="paragraphs" color="grey" size="XS_S_S">
                {t('Quiz.Progress.progress', {
                  progress,
                  time: timeRemaining,
                })}
              </Text>
            </FlexContainer>
          </Styled.Container>
        </FlexContainer>
      </Layout.Wrap>
    </OneColLayout>
  );
};

export default DataGatheringLoader;
